import { FC, KeyboardEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { cn } from '@distribute/frontend/utils';

import { IconMap } from '../../../shared/sprite';
import { ButtonSquare, Icon, Tooltip } from '../../../shared/ui';

import TextareaAutosize from 'react-textarea-autosize';

import { FeatureFlags, FeatureFlag } from '../../../entities/feature-flag';

import { useSend } from '../hooks';

import { chatModel } from '../model';
import { generateHTML } from '@tiptap/core';

import { generateMarkdown } from '@distribute/shared/tiptap/markdown';
import { sharedMainEditorExtensions } from '@distribute/shared/tiptap/extensions';

import {
  useCollaboration,
  getContentItems,
} from '../../../entities/collaboration';

const maxLength = 2000;

export const ChatBotInput: FC = () => {
  const dispatch = useDispatch();

  const conversationIsActive = useSelector(
    chatModel.selectors.selectConversationIsActive
  );
  const { provider } = useCollaboration();

  const { send } = useSend();

  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = () => {
    send(value.trim());
    setValue('');
  };

  const handleCopy = async () => {
    if (!provider) return;
    const tabs = getContentItems(provider);

    if (tabs.length > 0) {
      const html = generateHTML(
        tabs[0].contentJson,
        sharedMainEditorExtensions
      );
      const plain = generateMarkdown(tabs[0].contentJson);

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const length = value.trim().length;

  return (
    <div className="relative flex flex-col group">
      <TextareaAutosize
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoFocus
        maxLength={maxLength}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Ask anything..."
        className="pt-3 px-3 border-t border-x rounded-t-xl resize-none placeholder:text-gray-500 text-gray-900 focus:ring-0 focus:border-primary-600 focus:outline-none text-ellipsis disabled:bg-gray-50 disabled:text-gray-500 min-h-23 max-h-100"
      />
      <div
        className={cn(
          'flex items-center justify-between p-3 px-3 border-x border-b rounded-b-xl',
          { 'border-primary-600': isFocused }
        )}
      >
        <Tooltip
          sideOffset={4}
          hideArrow
          align="end"
          alignOffset={-8}
          className="py-1.5"
          trigger={
            <ButtonSquare
              variant="tertiary-gray"
              className="w-8 h-8 rounded-lg"
              onClick={() => dispatch(chatModel.actions.setScreen('settings'))}
              disabled={conversationIsActive}
            >
              <Icon glyph={IconMap.Settings04} width={20} />
            </ButtonSquare>
          }
        >
          Settings
        </Tooltip>

        <div className="ml-auto flex items-center gap-x-3 text-xs text-gray-600">
          <span className="text-xs">
            {length}/{maxLength}
          </span>
          <ButtonSquare
            variant="primary"
            className="w-8 h-8 rounded-lg"
            disabled={length === 0}
            onClick={handleSubmit}
          >
            <Icon
              glyph={IconMap.ArrowRight}
              width={20}
              className="-rotate-90"
            />
          </ButtonSquare>
          <FeatureFlag feature={FeatureFlags.MARKDOWN_CONTENT}>
            <ButtonSquare
              variant="primary"
              className="w-8 h-8 rounded-lg"
              onClick={handleCopy}
            >
              <Icon glyph={IconMap.Copy01} width={20} className="-rotate-90" />
            </ButtonSquare>
          </FeatureFlag>
        </div>
      </div>
    </div>
  );
};
